// Spaced-review persistence. Scheduling logic lives in utils/review-schedule
// (pure); this module just reads/writes reviews/{userId}/items/{itemId}.
import {
  doc, getDoc, setDoc, collection, query, where, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '../constants/collections';
import { scheduleNextReview, type ReviewItem } from '../utils/review-schedule';

function itemId(pathId: string, nodeId: string): string {
  return `${pathId}__${nodeId}`;
}

/**
 * Upsert a node's spaced-review schedule after a checkpoint is answered.
 * Reads the previous interval (if any) so a pass advances and a fail resets.
 * Fire-and-forget at the call site — review scheduling must never block learning.
 */
export async function upsertReviewAfterCheckpoint(params: {
  userId: string;
  contentId: string;
  pathId: string;
  nodeId: string;
  conceptIds: string[];
  label: string;
  score: number;
}): Promise<void> {
  const ref = doc(db, COLLECTIONS.REVIEWS, params.userId, 'items', itemId(params.pathId, params.nodeId));
  const snap = await getDoc(ref);
  const prev = snap.exists() ? (snap.data() as ReviewItem) : null;

  const sched = scheduleNextReview(
    prev ? { intervalIndex: prev.intervalIndex } : null,
    params.score,
    new Date(),
  );

  const item: ReviewItem = {
    userId: params.userId,
    contentId: params.contentId,
    pathId: params.pathId,
    nodeId: params.nodeId,
    conceptIds: params.conceptIds,
    label: params.label,
    ...sched,
  };

  await setDoc(ref, item, { merge: true });
}

/** Load the reviews that are due now (nextReviewAt <= now), soonest first. */
export async function loadDueReviews(userId: string, max = 12): Promise<ReviewItem[]> {
  const nowIso = new Date().toISOString();
  const q = query(
    collection(db, COLLECTIONS.REVIEWS, userId, 'items'),
    where('nextReviewAt', '<=', nowIso),
    orderBy('nextReviewAt', 'asc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as ReviewItem);
}
